import React from 'react';

/**
 * Editorial atmospheric background layer for text sections.
 * Very subtle — inspired by Aman Resorts / Mandarin editorial pages.
 * Renders an absolute-positioned image with a gradient fade toward the base color,
 * so text-side of the section remains fully legible.
 *
 * Props:
 *  - src: string (image URL)
 *  - side: 'right' (default) | 'left' | 'bottom'
 *  - base: 'white' (default) | 'gray' | 'black'
 *  - width: tailwind width class fragment (default '55%')
 *  - opacity: numeric 0..1 (default 0.08 for light, 0.16 for dark)
 */
export default function AtmoBg({
  src,
  side = 'right',
  base = 'white',
  width = 'md:w-[55%] w-[78%]',
  opacity,
}) {
  const baseColor =
    base === 'black' ? '#111111' :
    base === 'gray'  ? '#F5F5F5' :
    '#FFFFFF';
  const isDark = base === 'black';
  const eff = typeof opacity === 'number' ? opacity : (isDark ? 0.16 : 0.08);

  const gradient =
    side === 'left'
      ? `linear-gradient(to left, ${baseColor}, ${baseColor}00 78%)`
      : side === 'bottom'
      ? `linear-gradient(to top, ${baseColor}, ${baseColor}00 78%)`
      : `linear-gradient(to right, ${baseColor}, ${baseColor}b3 42%, ${baseColor}00 100%)`;

  const positionClass =
    side === 'left'
      ? `left-0 top-0 bottom-0 ${width}`
      : side === 'bottom'
      ? `left-0 right-0 bottom-0 h-[55%]`
      : `right-0 top-0 bottom-0 ${width}`;

  return (
    <div aria-hidden="true" className={`pointer-events-none absolute ${positionClass}`}>
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover"
        style={{ opacity: eff }}
        loading="lazy"
      />
      <div className="absolute inset-0" style={{ background: gradient }} />
    </div>
  );
}
